<template>
	<div
		v-if="product"
		class="flex flex-col w-full relative items-start group"
		:class="contentStyles || ''"
	>
		<NuxtLink
			:to="clickIsValid ? routeToProduct : ''"
			class="block w-full relative"
			@click="handleClick"
		>
			<ProductCardImage
				:product="product"
				:picture-size="pictureSize"
				:picture-aspect-ratio="pictureAspectRatio"
				:lazy-loading="lazyLoading"
				:current-image-src="currentImageSrc"
				:current-image-srcset="currentImageSrcSet"
				:current-image-is-norce="currentImageIsNorce"
				:current-image-provider="currentImageProvider"
				:product-flags="productFlags"
				:get-item-position="getItemPosition"
				:get-flag-data="getFlagData"
			/>
			<div class="flex flex-col mt-4 gap-2 items-start">
				<span class="text-left w-full">{{ product.Name }}</span>
				<Price
					:price="product.PriceIncVat!"
					:old-price="oldPriceValue"
					:show-save-text="false"
					:show-previous="false"
				/>
			</div>
		</NuxtLink>
		<ProductCardVariants
			v-model:hovered-color-value-id="hoveredColorValueId"
			:color-swatches="colorSwatches"
			:current-color-value-id="currentColorValueId"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ProductItem,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { useProductColors } from "~/composables/useProductColors";
import Price from "~/components/Product/ProductAttributes/Price.vue";
import ProductCardImage from "./ProductCardImage.vue";
import ProductCardVariants from "./ProductCardVariants.vue";

interface Props {
	product: Partial<FilteredProductItem> | ProductItem;
	pictureSize: string;
	pictureAspectRatio?: string;
	clickIsValid?: boolean;
	lazyLoading: boolean | undefined;
	contentStyles?: string;
	linkStyles?: string;
	variants?: FilteredProductItem[];
	allParametrics?: ParametricListValue[];
	showVariants?: boolean;
	useProductParentUrl?: boolean;
}
const {
	product,
	pictureAspectRatio,
	pictureSize,
	lazyLoading,
	clickIsValid = true,
	variants,
	allParametrics,
	useProductParentUrl
} = defineProps<Props>();
const emit = defineEmits(["product-click"]);

const productStore = useProductStore();
const appStore = useAppStore();
const config = useRuntimeConfig();
const { oldPrice } = useProductPrices(product);

const { hoveredColorValueId, colorSwatches, currentColorValueId } =
	useProductColors({
		product,
		variants,
		allParametrics
	});

const defaultImage = computed(() => {
	return productStore.defaultProductImage || "images/product/no_image.jpeg";
});

const localePath = useLocalePath();

const routeToProduct = computed(() =>
	localePath(
		`/${useProductParentUrl ? product.UniqueName : product.VariantUniqueName || product.UniqueName}`
	)
);

const productFlags = computed(() => {
	if (product?.FlagIdSeed) {
		return product.FlagIdSeed.split(",");
	}

	return [];
});

const appendParams = (src: string, params: string) => {
	const separator = src.includes("?") ? "&" : "?";
	return `${src}${separator}${params}`;
};

const isNorceCdnUrl = (src: string) => src.includes("cdn-norce.tech");

const buildNorceSrcSet = (src: string) => {
	if (!isNorceCdnUrl(src)) return "";
	const widths = [480, 768, 1024, 1440, 1920, 2560];
	return widths.map((w) => `${appendParams(src, `w=${w}`)} ${w}w`).join(", ");
};

const currentImageSrc = computed(() => {
	if (
		hoveredColorValueId.value &&
		colorSwatches.value &&
		colorSwatches.value.length > 0
	) {
		const hoveredSwatch = colorSwatches.value.find(
			(s) => s.valueId === hoveredColorValueId.value
		);
		if (hoveredSwatch?.variant) {
			const variantImageKey =
				(hoveredSwatch.variant as FilteredProductItem).VariantImageKey ||
				hoveredSwatch.variant.ImageKey;
			if (variantImageKey) {
				return `${config.public.norce.cdn}/${variantImageKey}?mode=max&format=webp`;
			}
			if (hoveredSwatch.variant.Files?.[0]?.Path) {
				return hoveredSwatch.variant.Files[0].Path + "?mode=max&format=webp";
			}
		}
	}

	const productImageKey =
		(product as FilteredProductItem)?.VariantImageKey || product?.ImageKey;
	if (productImageKey) {
		return `${config.public.norce.cdn}/${productImageKey}?mode=max&format=webp`;
	}

	if (product?.Files?.[0]?.Path) {
		return product.Files[0].Path + "?mode=max&format=webp";
	}

	return defaultImage.value;
});

const currentImageProvider = computed(() => {
	if (
		hoveredColorValueId.value &&
		colorSwatches.value &&
		colorSwatches.value.length > 0
	) {
		const hoveredSwatch = colorSwatches.value.find(
			(s) => s.valueId === hoveredColorValueId.value
		);
		if (hoveredSwatch?.variant) {
			const variantImageKey =
				(hoveredSwatch.variant as FilteredProductItem).VariantImageKey ||
				hoveredSwatch.variant.ImageKey;
			if (variantImageKey || hoveredSwatch.variant.Files?.[0]?.Path) {
				return undefined;
			}
		}
	}

	const productImageKey =
		(product as FilteredProductItem)?.VariantImageKey || product?.ImageKey;
	if (productImageKey || product?.Files?.[0]?.Path) {
		return undefined;
	}

	return "storyblok";
});

const currentImageIsNorce = computed(() =>
	isNorceCdnUrl(currentImageSrc.value)
);
const currentImageSrcSet = computed(() =>
	buildNorceSrcSet(currentImageSrc.value)
);

const oldPriceValue = computed(() => {
	const price = oldPrice.value;
	if (price === null) return undefined;
	if (typeof price === "string") {
		const parsed = parseFloat(price);
		return isNaN(parsed) ? undefined : parsed;
	}
	return price;
});

const { getItemPosition, getFlagData } = useApplicationFlags({
	appFlags: appStore.flags,
	productFlags: productFlags.value
});

const handleClick = () => {
	if (clickIsValid) {
		emit("product-click");
	}
};
</script>
